import React, { useCallback, useState } from 'react';
import { OctagonAlert, Unlock, Plus } from 'lucide-react';
import SectionShell from '../plan/SectionShell';
import { jobBriefApi } from '../../../services/jobBriefApi';

/**
 * HoldPointsSection — Phase D: active hold-points.
 *
 * Any crew member can create a hold at any moment. A hold BLOCKS the
 * place-keeping counter from advancing (enforced by PerformPhase) until
 * explicitly released.
 *
 * Everything is append-only: creating and releasing both post immutable
 * timeline events.
 */
export default function HoldPointsSection({ draftId, performState, ownerEmail, onChanged, isReadOnly }) {
  const holds = performState?.holds || [];
  const activeHolds = holds.filter((h) => !h.released);
  const releasedHolds = holds.filter((h) => h.released);
  const [reason, setReason] = useState('');
  const [stepIndex, setStepIndex] = useState('');
  const [busy, setBusy] = useState('');
  const [noteFor, setNoteFor] = useState({});

  const create = useCallback(async () => {
    if (!reason.trim() || !draftId) return;
    setBusy('create');
    try {
      await jobBriefApi.createHold(draftId, {
        reason:     reason.trim(),
        step_index: stepIndex === '' ? null : parseInt(stepIndex, 10),
        created_by: ownerEmail,
      });
      setReason('');
      setStepIndex('');
      onChanged?.();
    } finally {
      setBusy('');
    }
  }, [reason, stepIndex, draftId, ownerEmail, onChanged]);

  const release = useCallback(async (hold) => {
    if (!draftId) return;
    setBusy(hold.hold_id);
    try {
      await jobBriefApi.releaseHold(draftId, hold.hold_id, {
        released_by: ownerEmail,
        note:        (noteFor[hold.hold_id] || '').trim() || undefined,
      });
      setNoteFor((n) => ({ ...n, [hold.hold_id]: '' }));
      onChanged?.();
    } finally {
      setBusy('');
    }
  }, [draftId, ownerEmail, noteFor, onChanged]);

  return (
    <SectionShell
      icon={OctagonAlert}
      title="Active Holds"
      subtitle="Any hold BLOCKS step advancement. Release requires a qualified crew member."
      color={activeHolds.length ? '#fb7185' : '#94a3b8'}
      badge={activeHolds.length ? `${activeHolds.length} active` : 'All clear'}
      testId="jbv2-section-holds"
    >
      {activeHolds.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-holds-empty">
          No active holds. Work may proceed.
        </div>
      )}
      {activeHolds.map((h) => (
        <div
          key={h.hold_id}
          className="jbv2-row"
          style={{ borderColor: 'rgba(251,113,133,0.55)', background: 'rgba(251,113,133,0.06)' }}
          data-testid={`jbv2-hold-active-${h.hold_id}`}
        >
          <div className="jbv2-row-body">
            <div className="jbv2-row-cat" style={{ color: '#fca5a5' }}>
              HOLD
              {typeof h.step_index === 'number' && <> · Step {h.step_index + 1}</>}
            </div>
            <div className="jbv2-row-note">{h.reason}</div>
            <div className="jbv2-hp-row-meta">
              Created by {h.created_by} · {h.created_at && new Date(h.created_at).toLocaleTimeString()}
            </div>
          </div>
          {!isReadOnly && (
            <div className="jbv2-row-actions" style={{ flexDirection: 'column', alignItems: 'flex-end' }}>
              <input
                type="text"
                className="jbv2-field-input"
                style={{ maxWidth: 200, padding: '5px 8px', fontSize: 12 }}
                placeholder="Release note (optional)"
                value={noteFor[h.hold_id] || ''}
                onChange={(e) => setNoteFor((n) => ({ ...n, [h.hold_id]: e.target.value }))}
                data-testid={`jbv2-hold-release-note-${h.hold_id}`}
              />
              <button
                type="button"
                className="jbv2-row-btn"
                onClick={() => release(h)}
                disabled={busy === h.hold_id}
                data-testid={`jbv2-hold-release-btn-${h.hold_id}`}
              >
                <Unlock size={12} /> Release
              </button>
            </div>
          )}
        </div>
      ))}

      {releasedHolds.length > 0 && (
        <details style={{ marginTop: 4 }} data-testid="jbv2-holds-history">
          <summary style={{ cursor: 'pointer', color: '#94a3b8', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Released holds ({releasedHolds.length})
          </summary>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {releasedHolds.map((h) => (
              <div key={h.hold_id} className="jbv2-row jbv2-row--dismissed" data-testid={`jbv2-hold-released-${h.hold_id}`}>
                <div className="jbv2-row-body">
                  <div className="jbv2-row-cat">HOLD (released)</div>
                  <div className="jbv2-row-note">{h.reason}</div>
                  <div className="jbv2-hp-row-meta">
                    Released by {h.released_by}
                    {h.release_note && <> — &ldquo;{h.release_note}&rdquo;</>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {!isReadOnly && (
        <div className="jbv2-add-row" data-testid="jbv2-hold-create-form">
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is work stopping? (e.g. Adjacent bay is still energized)"
            data-testid="jbv2-hold-reason-input"
            aria-label="Hold reason"
          />
          <input
            type="number"
            min="1"
            value={stepIndex}
            onChange={(e) => setStepIndex(e.target.value)}
            placeholder="Step # (opt)"
            data-testid="jbv2-hold-step-input"
            aria-label="Step number"
            style={{ maxWidth: 130 }}
          />
          <button
            type="button"
            onClick={create}
            disabled={busy === 'create' || !reason.trim()}
            data-testid="jbv2-hold-create-btn"
          >
            <Plus size={13} /> Create hold
          </button>
        </div>
      )}
    </SectionShell>
  );
}
